import React, { FC, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import DownloadIcon from '../../../../assets/icons/DownloadIcon';
import LinkedInIcon from '../../../../assets/icons/LinkedInIcon';
import ShareIcon from '../../../../assets/icons/ShareIcon';
import { TestContext } from '../../../../context/TestContext';
import { generateImageKitUrl } from '../../../../modules/imageKit';
import { colorsPalette } from '../../../../theme';
import {
  generateCertificateName,
  getCertificatePDFUrl,
  downloadFile,
} from '../../../../utils/helpers';
import { LightTooltip } from '../../../UI/LightTooltip';
import useStyles from './styles';

interface CardTestResultsProps {
  hash?: string | null;
  passDate?: string | null;
  pathwayName?: string | null;
  tenantTitle?: string | null;
  tenantName?: string | null;
  cpdCredits?: string | null;
  pathwaySlug?: string | null;
  category?: string | null;
}

const CardTestResults: FC<CardTestResultsProps> = ({
  hash,
  passDate,
  pathwayName,
  tenantTitle,
  tenantName,
  cpdCredits,
  pathwaySlug,
  category,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { handleOpenLinkedInModal, handleOpenShareModal } = useContext(TestContext);

  const certName = generateCertificateName(tenantTitle, pathwayName);
  const certUrl = useMemo(
    () =>
      generateImageKitUrl({
        path: getCertificatePDFUrl(hash, tenantName),
        transformation: undefined,
      }),
    [hash, tenantName],
  );

  return (
    <div data-testid="cardTestResults" className={classes.wrapper}>
      <div className={classes.btnWrapper}>
        <LightTooltip
          classes={{
            tooltip: classes.customTooltip,
            popper: classes.customTooltipPopper,
          }}
          title={t('page.pathway.addToProfile') || ''}
          placement="top"
        >
          <button
            data-testid="addToProfileBtn"
            className={classes.btn}
            onClick={() => {
              handleOpenLinkedInModal({
                resultHash: hash,
                passDate: passDate,
                pathwayName: pathwayName,
              });
            }}
          >
            <LinkedInIcon color={colorsPalette.white} className={classes.icon} />
            <span className={classes.btnText}>{t('page.pathway.addToProfile')}</span>
          </button>
        </LightTooltip>
      </div>
      <div className={classes.btnWrapper}>
        <LightTooltip
          classes={{
            tooltip: classes.customTooltip,
            popper: classes.customTooltipPopper,
          }}
          title={t('page.pathway.cpdCertificate') || ''}
          placement="top"
        >
          <button
            data-testid="downloadCertificateBtn"
            className={classes.btn}
            onClick={() => downloadFile(certName, certUrl)}
          >
            <DownloadIcon color={colorsPalette.white} className={classes.icon} />
            <span className={classes.btnText}>{t('page.pathway.cpdCertificate')}</span>
          </button>
        </LightTooltip>
      </div>
      <div className={classes.btnWrapper}>
        <LightTooltip
          classes={{
            tooltip: classes.customTooltip,
            popper: classes.customTooltipPopper,
          }}
          title={t('page.pathway.share') || ''}
          placement="top"
        >
          <button
            data-testid="openShareModalBtn"
            className={classes.btn}
            onClick={() =>
              handleOpenShareModal(hash, pathwayName, cpdCredits, pathwaySlug, category)
            }
          >
            <ShareIcon color={colorsPalette.white} className={classes.icon} />
            <span className={classes.btnText}>{t('page.pathway.share')}</span>
          </button>
        </LightTooltip>
      </div>
    </div>
  );
};

export default CardTestResults;
